import React from 'react';
import { Filter, RotateCcw } from 'lucide-react';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTheme } from '../../contexts/ThemeContext';

interface SizeFilterProps {
  enabled: boolean;
  range: [number, number];
  globalBounds: { min: number; max: number };
  filteredOutCount: number;
  pendingUpdate: boolean;
  onRangeChange: (range: [number, number]) => void;
  onReset: () => void;
  onApplyNow: () => void;
}

export const SizeFilter: React.FC<SizeFilterProps> = ({
  enabled,
  range,
  globalBounds,
  filteredOutCount,
  pendingUpdate,
  onRangeChange,
  onReset,
  onApplyNow,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Don't render if no valid size data
  if (globalBounds.min === globalBounds.max) {
    return null;
  }

  const isFiltering = range[0] > globalBounds.min || range[1] < globalBounds.max;

  return (
    <div className="bg-background rounded-lg border p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-violet-500" />
          <span className="font-medium text-sm">Size Range Filter</span>
          {isFiltering && filteredOutCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {filteredOutCount} filtered out
            </Badge>
          )}
        </div>
        {/* Enhanced range badge with gradient and glow */}
        <span
          className="text-sm font-mono font-semibold px-3 py-1.5 rounded-lg truncate min-w-0"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.08) 100%)',
            color: isDark ? '#a78bfa' : '#7c3aed',
            border: isDark ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.25)',
            boxShadow: isDark ? '0 0 12px rgba(139, 92, 246, 0.2)' : '0 2px 8px rgba(139, 92, 246, 0.15)',
          }}
        >
          {range[0].toFixed(1)} - {range[1].toFixed(1)} μm
        </span>
      </div>

      {/* Slider */}
      <div className="space-y-2">
        <Slider
          min={globalBounds.min}
          max={globalBounds.max}
          step={0.1}
          value={range}
          onValueChange={(value) => onRangeChange(value as [number, number])}
          className="py-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{globalBounds.min.toFixed(1)} um</span>
          <span>{globalBounds.max.toFixed(1)} um</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="text-xs h-7 px-2"
          disabled={!isFiltering}
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset
        </Button>

        {pendingUpdate && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600 dark:text-blue-400">
              Histograms update in 5s...
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onApplyNow}
              className="text-xs h-7 px-2"
            >
              Apply Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
